// React hook wrapping validateAddress. Imperative shape: UI calls
// run(address) on a button click (or other discrete trigger) and
// watches { result, busy }. Not auto-fired on input change because
// the v1 stub provider doesn't have a debounce-friendly surface
// (no autocomplete, no streaming suggestions); an auto-fire would
// just churn the result on every keystroke. A real-vendor follow-on
// that supports autocomplete can add a separate useAddressAutocomplete
// hook alongside this one.
import { useCallback, useEffect, useState } from "react";
import {
  validateAddress,
  type AddressValidationResult,
  type StructuredAddress,
} from "@/lib/addressValidationClient";

export function useAddressValidation() {
  const [result, setResult] = useState<AddressValidationResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Bumped on every run() call so stale in-flight requests can be
  // dropped if a newer one starts before the older one resolves.
  const [runTick, setRunTick] = useState(0);

  const run = useCallback(async (input: StructuredAddress) => {
    const myTick = runTick + 1;
    setRunTick(myTick);
    setBusy(true);
    const res = await validateAddress(input);
    // Drop the result if a newer run() started while we were waiting.
    // Without this guard, fast double-clicks can render the older
    // result over the newer one.
    setRunTick((cur) => {
      if (cur !== myTick) return cur;
      setResult(res);
      setBusy(false);
      return cur;
    });
  }, [runTick]);

  const clear = useCallback(() => {
    setResult(null);
    setBusy(false);
  }, []);

  // Belt-and-suspenders: if the consumer unmounts while a run is in
  // flight, prevent the dropped-result branch from leaving busy=true
  // forever. (setState on unmounted components is a no-op in React 18+
  // but the local busy flag would otherwise stay stale on re-mount.)
  useEffect(() => {
    return () => {
      setBusy(false);
    };
  }, []);

  return { result, busy, run, clear };
}
